import fineLevyTemplate from './fineLevy.template.html';

/*******************************************************************************
 * FineLevy */
/**
 * @ngdoc   component
 * @module  csm.books.nsf
 * @name    csm.books.nsf.component:fineLevy
 * @description
 * This component allows the user to select a Fine Item, and apply a fine amount
 * to be attached to the NSF. The fine amount will be initially set using the
 * amount of the fine, the remaining NSF amount, and the bank charge (if applied).
 * This amount may be changed by the user to any value. The Fine will only be
 * applied after the Apply button is clicked, and the amount is displayed in the
 * collapse header.
 *******************************************************************************/
export const fineLevyComponent = {
  controller: Controller,
  template: fineLevyTemplate,
  bindings: {
    fine: '=',
    checkBalance: '<',
    bankCharge: '<',
    isNew: '=',
    fineTo: '<'
  }
};

/*******************************************************************************
 * Controller */
/**
 * Controls the data flow for the component.
 *******************************************************************************/
/* @ngInject */ function Controller($injector, $scope) {
  /** Global services object */
  var $svcs = { $ctrl: this, $globals: {}, $scope, $injector };

  $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs }));
  $injector.invoke(initGlobals, null, $svcs);
}

/*******************************************************************************
 * initCtrl */
/**
 * Initialize the controller objects
 *******************************************************************************/
/* @ngInject */ function initCtrl($ctrl, $injector, $svcs) {
  Object.assign($ctrl, {
    validators: {},
    /*--------------------*/
    /* Injector functions */
    /*--------------------*/
    applyFine: $injector.invoke(applyFine, null, $svcs),
    clearFine: $injector.invoke(clearFine, null, $svcs),
    entityLabel: $injector.invoke(entityLabel, null, $svcs),
    getEntities: $injector.invoke(getEntities, null, $svcs),
    getProducts: $injector.invoke(getProducts, null, $svcs),
    handleEntity: $injector.invoke(handleEntity, null, $svcs)
  });

  Object.defineProperty($ctrl, 'totalFine', {
    get: function () {
      return (
        $ctrl.fineAmount ||
        ($ctrl.item ? $ctrl.item.amount : 0) +
          $ctrl.checkBalance +
          $ctrl.bankCharge
      );
    },
    set: function (value) {
      $ctrl.fineAmount = value;
    }
  });
}

/*******************************************************************************
 * initGlobals */
/**
 * Initialize the global objects
 *******************************************************************************/
/* @ngInject */ function initGlobals($globals) {
  Object.assign($globals, {
    entityMakerActive: false
  });
}

FineLevyService.$inject = [];

/*******************************************************************************
 * FineLevyService */
/**
 * Service with a single function (clear) that will be called from the parent NSF
 * Check component to clear the fine inputs. The clear function is a placeholder
 * that will be replaced with the correct one when clearFine is called for the
 * first time.
 *******************************************************************************/
/* @ngInject */ export function FineLevyService() {
  return { clear: angular.noop };
}

/*******************************************************************************
 * applyFine */
/**
 * Validates the input fields and creates the fine object.
 *******************************************************************************/
/* @ngInject */ function applyFine($ctrl, Alert) {
  return function () {
    /*------------------*/
    /* Check validation */
    /*------------------*/
    $ctrl.validators.reset();
    if (!$ctrl.validators.validate()) return;

    $ctrl.fine = {
      amount: $ctrl.totalFine,
      itemID: $ctrl.item.id,
      entityID: $ctrl.fineTo.entity.id,
      notes: $ctrl.fineNotes
    };
  };
}

/*******************************************************************************
 * clearFine */
/**
 * Clears the editable fields, and the fine object.
 *******************************************************************************/
/* @ngInject */ function clearFine($ctrl, FineLevyService) {
  /*-----------------------------------------------*/
  /* Replace the clear function on the             */
  /* FineLevyService with this clearFine function. */
  /*-----------------------------------------------*/
  FineLevyService.clear = function () {
    $ctrl.clearFine();
  };

  return function () {
    $ctrl.validators.reset();

    $ctrl.item = null;
    $ctrl.fine.amount = 0;
    $ctrl.fine.itemID = NaN;
    $ctrl.fine.entityID = NaN;
    $ctrl.fineTo = null;
    $ctrl.fine.notes = $ctrl.fineNotes = null;
    $ctrl.checkBalance = 0;
  };
}

/*******************************************************************************
 * entityLabel */
/**
 * Builds the label for an entity. If the entity has a Student ID, this will be
 * added to the label.
 *
 * @param  $data  Entity object.
 *******************************************************************************/
/* @ngInject */ function entityLabel() {
  return function ($data) {
    if ($data && $data.entity)
      return '{0}{1}'.format(
        $data.entity.name,
        $data.entity.studentID ? ' ({0})'.format($data.entity.studentID) : ''
      );

    return null;
  };
}

/*******************************************************************************
 * getEntities */
/**
 * Gets a list of entities matching the `text` provided.
 *
 * @param  text  Text to use when search for entities.
 *******************************************************************************/
/* @ngInject */ function getEntities($q, BooksSvc) {
  return function (text) {
    /*---------------------------------------*/
    /* No input passed, return empty promise */
    /*---------------------------------------*/
    if (!text) return $q.resolve(null);

    var defer = $q.defer();
    BooksSvc.findAddressEntities(text, null, true).then(function (resp) {
      var entities = resp.data.sort(function (a, b) {
        return a.entity.fullName
          .toLowerCase()
          .localeCompare(b.entity.fullName.toLowerCase());
      });
      defer.resolve(entities);
    });
    return defer.promise;
  };
}

/*******************************************************************************
 * getProducts */
/**
 * Gets a list of products matching the `text` provided.
 *
 * @param  text  Text to use when search for products.
 *******************************************************************************/
/* @ngInject */ function getProducts($q, NSFChecksService) {
  return function (text) {
    /*----------------------------------------*/
    /* No input passed, return empty promise. */
    /*----------------------------------------*/
    if (!text) return $q.resolve(null);

    var defer = $q.defer();
    NSFChecksService.findFines(text).then(function (resp) {
      var fines = resp.sort(function (a, b) {
        return a.shortDescription
          .toLowerCase()
          .localeCompare(b.shortDescription.toLowerCase());
      });
      defer.resolve(fines);
    });
    return defer.promise;
  };
}

/*******************************************************************************
 * handleEntity */
/**
 * For the "Fine To" input field, if the typed in user does not exist It will ask
 * the user if they want to add the entity to our system. If not the user will
 * remain within the input field and whenever they click off the prompt to create
 * a user will continueously be asked until a valid entity is either created or
 * an existing entity is entered.
 *
 * @param  $event  Event object
 *******************************************************************************/
/* @ngInject */ function handleEntity(
  $globals,
  $ctrl,
  $scope,
  BooksSvc,
  BusyScreen,
  EntityMaker,
  SERVER,
  Alert
) {
  return function ($event) {
    /** standard initialization for Lazy type. */
    var ltID = $event.target.parentElement.id;
    var ltElem = angular.element(document.getElementById(ltID));
    var modelProp = ltElem.attr('ng-model');
    var addressEntity = $scope.$eval(modelProp);
    var restrictAllEntities =
      !!SERVER.appFlags.restrictVendors &&
      !!SERVER.appFlags.restrictIndividuals;

    /*-------------------------------------------------------------*/
    /* Clear out the entity values if this isn't an entity object. */
    /*-------------------------------------------------------------*/
    if (!addressEntity) {
      $ctrl.fineTo = null;

      return;
    }

    /*---------------------------------------------------------*/
    /* If the received value is an object we can assume        */
    /* that it is an existing entity and set that entity info  */
    /* as the current input fields value, otherwise we run     */
    /* the else statement to create a new entity.              */
    /*---------------------------------------------------------*/
    if (angular.isObject(addressEntity)) {
      $ctrl.fineTo = addressEntity;
    } else if (!restrictAllEntities) {
      /*---------------------------------------------------------*/
      /* Show EntityMaker Modal.                                 */
      /* This will offer the user the option to create the       */
      /* unrecognized entity within our system as a new entity   */
      /*---------------------------------------------------------*/
      $globals.entityMakerActive = true;

      /*--------------------------------------*/
      /* Build a new entity object with the   */
      /* results from EntityMaker and use it. */
      /*--------------------------------------*/
      EntityMaker.newEntity(addressEntity)
        .then(
          function (result) {
            BusyScreen.setBusy(true);

            if (result.added)
              return BooksSvc.getAddressEntityListItem(result.entity.id).then(
                function (resp) {
                  $ctrl.fineTo = resp.data;
                }
              );
          },

          /*----------------------------------------------------------------*/
          /* User declined / cancelled the create-entity prompt. Keep focus  */
          /* on the field (intentionally locking the user in) until they     */
          /* select a valid entity, create a new one, or clear the field —   */
          /* leaving unrecognized free text is not allowed.                  */
          /*----------------------------------------------------------------*/
          function () {
            $event.target.focus();
          }
        )
        .finally(function () {
          BusyScreen.setBusy(false);
          $globals.entityMakerActive = false;
          $event.target.focus();
        });
    } else {
      $event.target.value = '';
      Alert.info('You must select an existing entity.', 'Fine');
    }
  };
}
